import React, { createContext, useContext, useState, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { arrayMove } from '@dnd-kit/sortable';

import MembershipProductForm from "./MembershipProductForm";
import { DISPLAY, newMembershipProduct } from "./membershipFormLogic";

const MembershipProductNewContext = createContext();
let nextBenefitId = 0;

export function useMembershipProductNewContext() {
  return useContext(MembershipProductNewContext);
}

const MembershipProductNewProvider = (props) => {
  const {
    csrfToken,
    user,
    team,
    totalFeePercentage,
    venueOwnerships,
    membershipProduct: initialMembershipProduct,
    activeMemberCount = 0,
    householdSizes = [],
    otherMembershipProducts = [],
    membershipUpgradePolicy = "prorate",
    membershipUpgradeWindowDays = 90,
    membershipSettingsPath,
  } = props;

  const isEditing = !!initialMembershipProduct;

  const [membershipProduct, setMembershipProduct] = useState(() => initialMembershipProduct || newMembershipProduct(venueOwnerships));
  const [originalMembershipProduct, setOriginalMembershipProduct] = useState(membershipProduct);

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const updateMembershipProduct = useCallback((updates) => {
    setMembershipProduct(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const addBenefit = useCallback(() => {
    setMembershipProduct(prev => ({
      ...prev,
      membership_product_benefits: [
        ...prev.membership_product_benefits,
        { description: "", kind: DISPLAY, scans_per_day: null, position: prev.membership_product_benefits.length, _tempId: `new-${(nextBenefitId += 1)}` }
      ],
    }));
  }, []);

  const updateBenefit = useCallback((index, changes) => {
    setMembershipProduct(prev => ({
      ...prev,
      membership_product_benefits: prev.membership_product_benefits.map((b, i) =>
        i === index ? { ...b, ...changes } : b
      ),
    }));
  }, []);

  const removeBenefit = useCallback((index) => {
    setMembershipProduct(prev => {
      const benefits = [...prev.membership_product_benefits];
      const benefit = benefits[index];
      if (benefit.id) {
        benefits[index] = { ...benefit, _destroy: true };
      } else {
        benefits.splice(index, 1);
      }
      return { ...prev, membership_product_benefits: benefits };
    });
  }, []);

  const reorderBenefits = useCallback((oldIndex, newIndex) => {
    setMembershipProduct(prev => ({
      ...prev,
      membership_product_benefits: arrayMove(prev.membership_product_benefits, oldIndex, newIndex),
    }));
  }, []);

  const updateDefaultPrice = useCallback((field, value) => {
    setMembershipProduct(prev => ({
      ...prev,
      default_price: { ...prev.default_price, [field]: value },
    }));
  }, []);

  const updateTicketingPrice = useCallback((field, value) => {
    setMembershipProduct(prev => {
      const currentTicketingPrice = prev.ticketing_price_record || {
        amount_cents: 0, interval: "year", active: true
      };
      return {
        ...prev,
        ticketing_price_record: { ...currentTicketingPrice, [field]: value },
      };
    });
  }, []);

  const updateDefaultMonthlyPrice = useCallback((field, value) => {
    setMembershipProduct(prev => {
      const currentMonthlyPrice = prev.default_monthly_price || {
        amount_cents: 0, interval: "month", active: true
      };
      return {
        ...prev,
        default_monthly_price: { ...currentMonthlyPrice, [field]: value },
      };
    });
  }, []);

  const updateTicketingMonthlyPrice = useCallback((field, value) => {
    setMembershipProduct(prev => {
      const currentTicketingMonthlyPrice = prev.ticketing_monthly_price_record || {
        amount_cents: 0, interval: "month", active: true
      };
      return {
        ...prev,
        ticketing_monthly_price_record: { ...currentTicketingMonthlyPrice, [field]: value },
      };
    });
  }, []);

  const replaceMembershipProduct = useCallback((newProduct) => {
    setMembershipProduct(newProduct);
    setOriginalMembershipProduct(JSON.parse(JSON.stringify(newProduct)));
  }, []);

  const discardChanges = useCallback(() => {
    setMembershipProduct(JSON.parse(JSON.stringify(originalMembershipProduct)));
  }, [originalMembershipProduct]);

  const value = {
    csrfToken,
    user,
    team,
    totalFeePercentage,
    venueOwnerships,
    membershipProduct,
    originalMembershipProduct,
    updateMembershipProduct,
    addBenefit,
    updateBenefit,
    removeBenefit,
    reorderBenefits,
    updateDefaultPrice,
    updateTicketingPrice,
    updateDefaultMonthlyPrice,
    updateTicketingMonthlyPrice,
    replaceMembershipProduct,
    discardChanges,
    activeMemberCount,
    householdSizes,
    otherMembershipProducts,
    membershipUpgradePolicy,
    membershipUpgradeWindowDays,
    membershipSettingsPath,
    isEditing,
    isSaving,
    setIsSaving,
    errors,
    setErrors,
  };

  return (
    <MembershipProductNewContext.Provider value={value}>
      {props.children}
    </MembershipProductNewContext.Provider>
  );
};

const MembershipProductNewContainer = (props) => (
  <MembershipProductNewProvider {...props}>
    <ToastContainer icon={false} />
    <MembershipProductForm />
  </MembershipProductNewProvider>
);

export default MembershipProductNewContainer;
